<template>
	<v-container fluid class = "pa-0 access-control-face-roster">
		<v-card outlined class = "access-control-face-roster-card">
			<v-card-title class = "pa-1 px-2 access-control-face-roster-header vue-draggable-handle">
				<div class = "access-control-face-header-icon">
					<v-icon small color = "primary">mdi-face-recognition</v-icon>
				</div>
				<div class = "access-control-face-header-copy">
					<div class = "access-control-face-title">Rostros del terminal</div>
					<div class = "access-control-face-subtitle">{{ deviceName }}</div>
				</div>
				<v-spacer></v-spacer>
				<v-chip x-small :color = "statusColor" class = "mr-1 access-control-face-header-chip">
					{{ statusLabel }}
				</v-chip>
				<v-btn
					icon
					x-small
					:loading = "loading"
					class = "access-control-face-refresh"
					aria-label = "Actualizar listado de rostros"
					title = "Actualizar listado de rostros"
					@click = "loadRoster"
				>
					<v-icon x-small>mdi-refresh</v-icon>
				</v-btn>
			</v-card-title>

			<v-card-text class = "pa-1 access-control-face-roster-body">
				<div class = "access-control-face-toolbar">
					<div class = "access-control-face-summary">
						<div class = "access-control-face-count">{{ faceCountLabel }}</div>
						<div class = "access-control-face-count-detail">
							<span>{{ filteredRegisteredFaces.length }} registrados</span>
							<span>{{ filteredTerminalOnlyFaces.length }} falta importar</span>
							<span>{{ filteredBackendOnlyFaces.length }} ya no están en el terminal</span>
						</div>
					</div>
					<div class = "access-control-face-search-wrap">
						<v-text-field
							v-model = "filterQuery"
							dense
							outlined
							clearable
							hide-details
							prepend-inner-icon = "mdi-magnify"
							label = "Buscar por nombre o FPID"
							class = "access-control-face-filter"
						></v-text-field>
					</div>
					<v-btn
						color = "primary"
						x-small
						class = "access-control-face-sync"
						:disabled = "!deviceId || loading || syncLoading"
						:loading = "syncLoading"
						:aria-label = "mlText('access_control_face_roster_sync_terminal_users')"
						:title = "mlText('access_control_face_roster_sync_terminal_users')"
						@click = "syncTerminalUsers"
					>
						<v-icon x-small left>mdi-sync</v-icon>
						{{ mlText('access_control_face_roster_sync_terminal_users') }}
					</v-btn>
				</div>

				<div v-if = "hasSelectableFaces" class = "access-control-face-selection-bar">
					<div class = "caption font-weight-medium">
						{{ selectedCount }} persona{{ selectedCount === 1 ? '' : 's' }} seleccionada{{ selectedCount === 1 ? '' : 's' }} para importar
					</div>
					<div class = "access-control-face-selection-actions">
						<v-btn text x-small :disabled = "!selectedCount || importLoading" @click = "clearSelection">Limpiar selección</v-btn>
						<v-btn color = "primary" x-small :disabled = "!selectedCount" :loading = "importLoading" @click = "openImportDialog">Importar a lista</v-btn>
					</div>
				</div>

				<v-alert v-if = "importSuccess" dense outlined type = "success" class = "mb-2 access-control-face-note">
					{{ importSuccess }}
				</v-alert>
				<v-alert v-if = "importError" dense outlined type = "warning" class = "mb-2 access-control-face-note">
					{{ importError }}
				</v-alert>

				<v-alert v-if = "reasonText" dense outlined type = "warning" class = "mb-2 access-control-face-note">
					{{ reasonText }}
				</v-alert>

				<div v-if = "loading" class = "text-center pa-4 text--secondary">
					<v-progress-circular indeterminate size = "22" width = "2" color = "primary"></v-progress-circular>
					<div class = "caption mt-2">Cargando listado de rostros...</div>
				</div>

				<v-alert v-else-if = "loadError" dense outlined type = "warning" class = "mb-0">
					{{ loadError }}
				</v-alert>

				<div v-else-if = "!hasAnyFaces" class = "access-control-face-empty-state">
					<v-icon size = "32">mdi-account-search</v-icon>
					<div class = "font-weight-medium mt-2">No hay rostros para mostrar</div>
					<div class = "caption text--secondary">No hay rostros registrados en el terminal ni en el sistema.</div>
				</div>


				<div v-else class = "access-control-face-roster-list">
					<div v-if = "partialRosterText" class = "caption warning--text mb-2">
						{{ partialRosterText }}
					</div>
					<v-card
						v-for = "section in faceSections"
						:key = "section.key"
						outlined
						:class = "section.cardClass"
					>
						<div class = "access-control-face-section-header">
							<div>
								<div :class = "section.titleClass">{{ section.title }} ({{ section.faces.length }})</div>
								<div class = "caption text--secondary">{{ section.subtitle }}</div>
							</div>
							<v-chip x-small outlined :color = "section.chipColor">{{ section.faces.length }}</v-chip>
						</div>
						<div v-if = "section.faces.length" class = "access-control-face-card-grid">
							<v-card
								v-for = "(face, index) in section.faces"
								:key = "`${section.key}-${faceKey(face, index)}`"
								outlined
								class = "access-control-face-person-card"
								role = "button"
								tabindex = "0"
								:aria-label = "`Ver detalle de ${face.name || 'Persona sin nombre'}`"
								@click = "openFaceDetails(face, index)"
								@keydown.enter.prevent = "openFaceDetails(face, index)"
								@keydown.space.prevent = "openFaceDetails(face, index)"
							>
								<v-btn
									v-if = "isSelectableFace(face)"
									icon
									x-small
									class = "access-control-face-select-toggle"
									:aria-label = "selectionLabel(face)"
									:title = "selectionLabel(face)"
									@click.stop = "toggleSelection(face)"
								>
									<v-icon x-small>{{ isSelected(face) ? 'mdi-checkbox-marked-circle' : 'mdi-checkbox-blank-circle-outline' }}</v-icon>
								</v-btn>
								<div class = "access-control-face-photo-wrap">
									<img
										v-if = "faceImageSource(face, index)"
										:src = "faceImageSource(face, index)"
									:alt = "`Foto del rostro de ${face.name || 'Persona sin nombre'}`"
										class = "access-control-face-photo"
										@error = "markImageUnavailable(face, index)"
									>
									<v-avatar v-else size = "36" class = "access-control-face-avatar">
										<v-icon>mdi-account</v-icon>
									</v-avatar>
								</div>
								<div class = "access-control-face-person-info">
								<div class = "font-weight-medium access-control-face-person-name">{{ face.name || 'Persona sin nombre' }}</div>
									<div class = "access-control-face-fpid">FPID {{ face.FPID || '—' }}</div>
								</div>
								<v-chip x-small outlined :color = "statusChipColor(face)" class = "access-control-face-source-chip">{{ statusText(face) }}</v-chip>
							</v-card>
						</div>
						<div v-else class = "access-control-face-section-empty">{{ section.emptyText }}</div>
					</v-card>
				</div>
			</v-card-text>
		</v-card>

		<v-dialog v-model = "detailDialog" max-width = "520">
			<v-card v-if = "selectedFace" class = "access-control-face-detail-card">
				<v-card-title class = "access-control-face-detail-header">
					<div>
						<div class = "access-control-face-detail-title">{{ selectedFace.name || 'Persona sin nombre' }}</div>
						<div class = "caption text--secondary">FPID {{ selectedFace.FPID || '—' }}</div>
					</div>
					<v-spacer></v-spacer>
					<v-btn icon small aria-label = "Cerrar detalle del rostro" title = "Cerrar detalle del rostro" @click = "closeFaceDetails">
						<v-icon small>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-card-text class = "access-control-face-detail-body">
					<div class = "access-control-face-detail-photo-wrap">
						<img
							v-if = "selectedFaceImageSource"
							:src = "selectedFaceImageSource"
							:alt = "`Foto del rostro de ${selectedFace.name || 'Persona sin nombre'}`"
							class = "access-control-face-detail-photo"
							@error = "markImageUnavailable(selectedFace, selectedFaceIndex)"
						>
						<v-avatar v-else size = "120" class = "access-control-face-detail-avatar">
							<v-icon size = "64">mdi-account</v-icon>
						</v-avatar>
					</div>

					<div class = "access-control-face-detail-list">
						<div class = "access-control-face-detail-row">
							<span class = "text--secondary">Nombre</span>
						<strong>{{ selectedFace.name || 'Persona sin nombre' }}</strong>
						</div>
						<div class = "access-control-face-detail-row">
							<span class = "text--secondary">FPID</span>
							<strong>{{ selectedFace.FPID || '—' }}</strong>
						</div>
						<div class = "access-control-face-detail-row">
							<span class = "text--secondary">Estado</span>
							<v-chip x-small outlined :color = "statusChipColor(selectedFace)">{{ statusText(selectedFace) }}</v-chip>
						</div>
						<div class = "access-control-face-detail-row">
							<span class = "text--secondary">Terminal</span>
							<strong>{{ presenceText(selectedFace, 'terminal') }}</strong>
						</div>
						<div class = "access-control-face-detail-row">
						<span class = "text--secondary">Sistema</span>
							<strong>{{ presenceText(selectedFace, 'backend') }}</strong>
						</div>
					</div>
				</v-card-text>
			</v-card>
		</v-dialog>

		<v-dialog v-model = "importDialog" max-width = "460">
			<v-card class = "access-control-face-import-card">
				<v-card-title class = "access-control-face-detail-header">
					<div>
						<div class = "access-control-face-detail-title">Importar personas</div>
						<div class = "caption text--secondary">Elegí la lista donde se van a agregar.</div>
					</div>
					<v-spacer></v-spacer>
					<v-btn icon small aria-label = "Cerrar importación" title = "Cerrar importación" @click = "closeImportDialog">
						<v-icon small>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text>
					<div class = "caption mb-2">{{ selectedCount }} persona{{ selectedCount === 1 ? '' : 's' }} lista{{ selectedCount === 1 ? '' : 's' }} para importar.</div>
					<v-select
						v-model = "selectedListId"
						:items = "faceListOptions"
						:loading = "listsLoading"
						item-text = "text"
						item-value = "value"
						dense
						outlined
						hide-details = "auto"
						label = "Lista de rostros"
						no-data-text = "No hay listas de rostros disponibles"
					></v-select>
					<v-alert v-if = "importError" dense outlined type = "warning" class = "mt-3 mb-0">{{ importError }}</v-alert>
				</v-card-text>
				<v-card-actions class = "px-4 pb-4">
					<v-spacer></v-spacer>
					<v-btn text small :disabled = "importLoading" @click = "closeImportDialog">Cancelar</v-btn>
					<v-btn color = "primary" small :disabled = "!canConfirmImport" :loading = "importLoading" @click = "confirmImport">Importar</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script>
	import AccessControlService from '@/services/accessControl.service.js'
	import ListService from '@/services/list.service.js'

	export const FACE_ROSTER_LIMIT = 100

	export default {
		name: 'access_control_face_roster',
		props: {
			info_device: {
				type: Object,
				default: () => ({}),
			},
			info_to_device: {
				type: Object,
				default: () => ({}),
			},
		},
		data: () => ({
			loading: false,
			loadError: '',
			roster: null,
			filterQuery: '',
			unavailableImages: {},
			detailDialog: false,
			selectedFace: null,
			selectedFaceIndex: 0,
			selectedFaceKeys: {},
			importDialog: false,
			faceLists: [],
			selectedListId: '',
			listsLoading: false,
			importLoading: false,
			syncLoading: false,
			importError: '',
			importSuccess: '',
		}),
		computed: {
			device(){
				return this.info_device && Object.keys(this.info_device).length ? this.info_device : (this.info_to_device || {})
			},
			deviceId(){
				return this.device && (this.device.deviceId || this.device.device_id || this.device.id)
			},
			deviceName(){
				return (this.roster && this.roster.name) || (this.device && this.device.name) || 'Access terminal'
			},
			status(){
				return (this.roster && this.roster.status) || 'unknown'
			},
			statusLabel(){
				return String(this.status || 'unknown').toUpperCase()
			},
			statusColor(){
				if (this.status === 'online') return 'success'
				if (this.status === 'offline') return 'error'
				return 'warning'
			},
			faces(){
				return this.roster && Array.isArray(this.roster.faces) ? this.roster.faces : []
			},
			registeredFaces(){
				if (this.roster && Object.prototype.hasOwnProperty.call(this.roster, 'registeredFaces')) {
					return Array.isArray(this.roster.registeredFaces) ? this.roster.registeredFaces : []
				}
				return this.faces
			},
			unregisteredFaces(){
				return this.roster && Array.isArray(this.roster.unregisteredFaces) ? this.roster.unregisteredFaces : []
			},
			hasGroupedRoster(){
				return Boolean(this.roster && (
					Object.prototype.hasOwnProperty.call(this.roster, 'registeredFaces') ||
					Object.prototype.hasOwnProperty.call(this.roster, 'unregisteredFaces')
				))
			},
			hasAnyFaces(){
				return this.registeredFaces.length > 0 || this.unregisteredFaces.length > 0 || this.faces.length > 0
			},
			filterNeedle(){
				return String(this.filterQuery || '').trim().toLowerCase()
			},
			filteredRegisteredFaces(){
				return this.filterFaces(this.registeredFaces)
			},
			filteredUnregisteredFaces(){
				return this.filterFaces(this.unregisteredFaces)
			},
			filteredTerminalOnlyFaces(){
				return this.filterFaces(this.unregisteredFaces.filter(face => !face || face.registrationStatus !== 'backend_only'))
			},
			filteredBackendOnlyFaces(){
				return this.filterFaces(this.unregisteredFaces.filter(face => face && face.registrationStatus === 'backend_only'))
			},
			faceSections(){
				return [
					{
						key: 'registered',
						faces: this.filteredRegisteredFaces,
						title: 'Registrados',
						subtitle: 'Presentes en el terminal y en el sistema',
						cardClass: 'access-control-face-section-card access-control-face-section-registered mb-2',
						titleClass: 'access-control-face-section-title success--text',
						chipColor: 'success',
						emptyText: 'No hay rostros registrados que coincidan con el filtro.',
					},
					{
						key: 'terminal-only',
						faces: this.filteredTerminalOnlyFaces,
						title: 'Falta importar',
						subtitle: 'Presentes en el terminal, pendientes de importar al sistema',
						cardClass: 'access-control-face-section-card access-control-face-section-terminal-only mb-2',
						titleClass: 'access-control-face-section-title error--text',
						chipColor: 'error',
						emptyText: 'No hay rostros pendientes de importar que coincidan con el filtro.',
					},
					{
						key: 'backend-only',
						faces: this.filteredBackendOnlyFaces,
						title: 'Solo en el sistema',
						subtitle: 'Presentes en el sistema, ausentes del terminal',
						cardClass: 'access-control-face-section-card access-control-face-section-backend-only',
						titleClass: 'access-control-face-section-title warning--text',
						chipColor: 'warning',
						emptyText: 'No hay personas ausentes del terminal que coincidan con el filtro.',
					},
				]
			},
			summaryCount(){
				if (this.hasGroupedRoster) return this.registeredFaces.length + this.unregisteredFaces.length
				return this.faceCount
			},
			faceCount(){
				if (this.roster && this.roster.faceCount !== undefined && this.roster.faceCount !== null) {
					return this.roster.faceCount
				}
				return this.registeredFaces.length + this.unregisteredFaces.length || this.faces.length
			},
			displayedCount(){
				return this.filteredRegisteredFaces.length + this.filteredTerminalOnlyFaces.length + this.filteredBackendOnlyFaces.length
			},
			faceCountLabel(){
				const total = Number(this.summaryCount)
				const noun = this.hasGroupedRoster ? 'persona' : 'rostro'
				const totalLabel = `${this.summaryCount} ${noun}${total === 1 ? '' : 's'}`
				return this.displayedCount !== total ? `${totalLabel} · mostrando ${this.displayedCount}` : totalLabel
			},
			partialRosterText(){
				const meta = this.roster && this.roster.rosterMeta ? this.roster.rosterMeta : null
				if (!meta || !meta.partial) return ''
				return `Mostrando ${meta.returned || this.displayedCount} de ${meta.total || this.faceCount} personas registradas. La respuesta del terminal fue parcial${meta.truncated ? ' porque se alcanzó el límite de seguridad' : ''}.`
			},
			reasonText(){
				return this.roster && this.roster.reason ? this.roster.reason : ''
			},
			selectedFaceImageSource(){
				return this.selectedFace ? this.faceImageSource(this.selectedFace, this.selectedFaceIndex) : ''
			},
			selectableTerminalOnlyFaces(){
				return this.unregisteredFaces.filter(face => this.isSelectableFace(face))
			},
			hasSelectableFaces(){
				return this.selectableTerminalOnlyFaces.length > 0
			},
			selectedFaces(){
				return this.selectableTerminalOnlyFaces.filter(face => this.isSelected(face))
			},
			selectedCount(){
				return this.selectedFaces.length
			},
			faceListOptions(){
				return this.faceLists.map(list => ({
					text: list.name || `Lista ${list.id}`,
					value: list.id,
				}))
			},
			canConfirmImport(){
				return Boolean(this.selectedCount && this.selectedListId && !this.importLoading)
			},
		},
		watch: {
			deviceId(){
				this.loadRoster()
			},
		},
		mounted(){
			this.loadRoster()
		},
		methods: {
			mlText(key){
				if (this.$ml && typeof this.$ml.get === 'function') return this.$ml.get(key)
				return key
			},
			async loadRoster(){
				if (!this.deviceId) {
					this.roster = null
					this.loadError = 'No se encontró el identificador del terminal.'
					return
				}
				this.loading = true
				this.loadError = ''
				try {
					const res = await AccessControlService.getFaceRoster(this.deviceId, FACE_ROSTER_LIMIT)
					this.roster = res && res.data ? res.data : null
					this.pruneSelection()
				} catch (error) {
					this.roster = null
					this.loadError = 'No se pudo cargar el listado de rostros.'
				} finally {
					this.loading = false
				}
			},
			faceKey(face, index){
				return (face && (face.FPID || face.name || face.imageBase64)) || `face-${index}`
			},
			faceImageSource(face, index = 0){
				if (!face) return ''
				if (this.unavailableImages[this.faceKey(face, index)]) return ''
				if (!face.imageBase64) return ''
				const image = String(face.imageBase64)
				return image.indexOf('data:') === 0 ? image : `data:image/jpeg;base64,${image}`
			},
			markImageUnavailable(face, index){
				this.$set(this.unavailableImages, this.faceKey(face, index), true)
			},
			filterFaces(faces){
				if (!this.filterNeedle) return faces
				return faces.filter(face => {
					const name = String((face && face.name) || '').toLowerCase()
					const fpid = String((face && face.FPID) || '').toLowerCase()
					return name.indexOf(this.filterNeedle) !== -1 || fpid.indexOf(this.filterNeedle) !== -1
				})
			},
			statusText(face){
				if (face && face.registrationStatus === 'registered') return 'Registrado'
				if (face && face.registrationStatus === 'backend_only') return 'Solo en el sistema'
				if (face && face.registrationStatus === 'terminal_only') return 'Falta importar'
				return 'Registrado'
			},
			statusChipColor(face){
				if (face && face.registrationStatus === 'backend_only') return 'warning'
				if (face && face.registrationStatus === 'terminal_only') return 'error'
				return 'success'
			},
			openFaceDetails(face, index){
				this.selectedFace = face
				this.selectedFaceIndex = index
				this.detailDialog = true
			},
			closeFaceDetails(){
				this.detailDialog = false
			},
			presenceText(face, source){
				if (!face) return '—'
				if (face.registrationStatus === 'registered') return 'Sí'
				if (face.registrationStatus === 'terminal_only') return source === 'terminal' ? 'Sí' : 'No'
				if (face.registrationStatus === 'backend_only') return source === 'backend' ? 'Sí' : 'No'
				return '—'
			},
			isSelectableFace(face){
				return Boolean(face && face.registrationStatus === 'terminal_only')
			},
			selectionKey(face){
				return face && face.FPID ? String(face.FPID) : ''
			},
			isSelected(face){
				const key = this.selectionKey(face)
				return Boolean(key && this.selectedFaceKeys[key])
			},
			selectionLabel(face){
				return `${this.isSelected(face) ? 'Quitar' : 'Seleccionar'} ${face && face.name ? face.name : 'persona'} para importar`
			},
			toggleSelection(face){
				if (!this.isSelectableFace(face)) return
				const key = this.selectionKey(face)
				if (!key) return
				if (this.selectedFaceKeys[key]) {
					this.$delete(this.selectedFaceKeys, key)
				} else {
					this.$set(this.selectedFaceKeys, key, true)
				}
				this.importSuccess = ''
			},
			clearSelection(){
				this.selectedFaceKeys = {}
				this.importSuccess = ''
			},
			pruneSelection(){
				const available = new Set(this.selectableTerminalOnlyFaces.map(face => this.selectionKey(face)).filter(Boolean))
				Object.keys(this.selectedFaceKeys).forEach(key => {
					if (!available.has(key)) this.$delete(this.selectedFaceKeys, key)
				})
			},
			async openImportDialog(){
				if (!this.selectedCount) return
				this.importDialog = true
				this.importError = ''
				await this.loadFaceLists()
			},
			closeImportDialog(){
				if (this.importLoading) return
				this.importDialog = false
			},
			async loadFaceLists(){
				this.listsLoading = true
				this.importError = ''
				try {
					const res = await ListService.getList(1)
					const rows = res && res.data && Array.isArray(res.data.rows) ? res.data.rows : []
					this.faceLists = rows.filter(list => !list.type || String(list.type) === 'face')
					if (!this.selectedListId && this.faceLists.length) this.selectedListId = this.faceLists[0].id
				} catch (error) {
					this.faceLists = []
					this.importError = 'No se pudieron cargar las listas de rostros.'
				} finally {
					this.listsLoading = false
				}
			},
			async confirmImport(){
				if (!this.canConfirmImport) return
				this.importLoading = true
				this.importError = ''
				this.importSuccess = ''
				try {
					const faces = this.selectedFaces.map(face => ({
						FPID: face.FPID,
						name: face.name,
						imageBase64: face.imageBase64,
					}))
					const res = await AccessControlService.importFaceRoster(this.deviceId, { listId: this.selectedListId, faces })
					const results = res && res.data && Array.isArray(res.data.results) ? res.data.results : []
					const imported = results.filter(item => item && item.status !== 'skipped').length || faces.length
					this.importDialog = false
					this.clearSelection()
					this.importSuccess = `${imported} persona${imported === 1 ? '' : 's'} importada${imported === 1 ? '' : 's'} y agregada${imported === 1 ? '' : 's'} a la lista.`
					await this.loadRoster()
				} catch (error) {
					this.importError = 'No se pudo importar la selección.'
				} finally {
					this.importLoading = false
				}
			},
			async syncTerminalUsers(){
				if (!this.deviceId || this.syncLoading) return
				this.syncLoading = true
				this.importError = ''
				this.importSuccess = ''
				try {
					await AccessControlService.syncFaceRoster(this.deviceId)
					this.clearSelection()
					this.importSuccess = this.mlText('access_control_face_roster_sync_success')
					await this.loadRoster()
				} catch (error) {
					this.importError = this.mlText('access_control_face_roster_sync_error')
				} finally {
					this.syncLoading = false
				}
			},
		},
	}
</script>

<style scoped>
	.access-control-face-roster{
		height: 100%;
		min-height: 0;
		overflow: hidden;
	}

	.access-control-face-roster-card{
		border-radius: 10px;
		display: flex;
		flex-direction: column;
		height: 100%;
		max-height: 100%;
		min-height: 0;
		overflow: hidden;
	}

	.access-control-face-roster-header{
		align-items: center;
		border-bottom: thin solid currentColor;
		gap: 8px;
		min-height: 38px;
	}

	.access-control-face-header-icon{
		align-items: center;
		border: thin solid currentColor;
		border-radius: 8px;
		display: flex;
		height: 24px;
		justify-content: center;
		width: 24px;
	}

	.access-control-face-header-copy{
		line-height: 1.1;
		min-width: 0;
	}

	.access-control-face-title{
		font-size: 0.78rem;
		font-weight: 700;
		letter-spacing: 0.01em;
	}

	.access-control-face-subtitle{
		font-size: 0.64rem;
		max-width: 220px;
		opacity: 0.72;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.access-control-face-header-chip{
		font-weight: 700;
	}

	.access-control-face-refresh{
		color: inherit !important;
	}

	.access-control-face-roster-body{
		display: flex;
		flex: 1 1 auto;
		flex-direction: column;
		min-height: 0;
		overflow: hidden;
	}

	.access-control-face-toolbar{
		align-items: center;
		border: thin solid currentColor;
		border-radius: 10px;
		display: flex;
		gap: 8px;
		margin-bottom: 6px;
		padding: 6px 8px;
	}

	.access-control-face-summary{
		flex: 1 1 auto;
		min-width: 160px;
	}

	.access-control-face-count{
		font-size: 0.82rem;
		font-weight: 700;
		line-height: 1.2;
	}

	.access-control-face-count-detail{
		display: flex;
		flex-wrap: wrap;
		font-size: 0.64rem;
		gap: 4px 10px;
		line-height: 1.2;
		margin-top: 2px;
		opacity: 0.72;
	}

	.access-control-face-search-wrap{
		flex: 0 1 300px;
		min-width: 180px;
	}

	.access-control-face-filter{
		border-radius: 8px;
	}

	.access-control-face-note{
		font-size: 0.72rem;
	}

	.access-control-face-selection-bar{
		align-items: center;
		border: thin solid currentColor;
		border-radius: 10px;
		display: flex;
		justify-content: space-between;
		margin-bottom: 6px;
		padding: 6px 8px;
	}

	.access-control-face-selection-actions{
		display: flex;
		gap: 6px;
	}

	.access-control-face-roster-list{
		flex: 1 1 auto;
		min-height: 0;
		overflow-y: auto;
		padding-right: 2px;
	}

	.access-control-face-section-card{
		border-radius: 10px;
		padding: 6px;
	}

	.access-control-face-section-registered{
		border-left: 3px solid var(--v-success-base) !important;
	}

	.access-control-face-section-terminal-only{
		border-left: 3px solid var(--v-error-base) !important;
	}

	.access-control-face-section-backend-only{
		border-left: 3px solid var(--v-warning-base) !important;
	}

	.access-control-face-section-header{
		align-items: flex-start;
		display: flex;
		justify-content: space-between;
		margin-bottom: 5px;
	}

	.access-control-face-section-title{
		font-size: 0.74rem;
		font-weight: 600;
		line-height: 1.15;
	}

	.access-control-face-card-grid{
		display: grid;
		gap: 5px;
		grid-template-columns: repeat(auto-fill, minmax(168px, 1fr));
	}

	.access-control-face-person-card{
		align-items: center;
		border-radius: 10px;
		cursor: pointer;
		display: flex;
		gap: 7px;
		min-height: 52px;
		padding: 5px;
	}

	.access-control-face-select-toggle{
		flex: 0 0 auto;
	}

	.access-control-face-person-card:focus-visible{
		outline: 2px solid var(--v-primary-base);
		outline-offset: 2px;
	}

	.access-control-face-photo-wrap{
		flex: 0 0 auto;
	}

	.access-control-face-photo{
		border: thin solid currentColor;
		border-radius: 9px;
		display: block;
		height: 36px;
		object-fit: cover;
		width: 36px;
	}

	.access-control-face-avatar{
		border-radius: 9px !important;
	}

	.access-control-face-person-info{
		flex: 1 1 auto;
		min-width: 0;
	}

	.access-control-face-person-name{
		font-size: 0.74rem;
		line-height: 1.2;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.access-control-face-fpid{
		font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
		font-size: 0.62rem;
		line-height: 1.25;
		opacity: 0.72;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.access-control-face-source-chip{
		flex: 0 0 auto;
		font-size: 0.62rem !important;
		max-width: 92px;
	}

	.access-control-face-section-empty{
		border: 1px dashed currentColor;
		border-radius: 8px;
		font-size: 0.72rem;
		opacity: 0.72;
		padding: 10px;
		text-align: center;
	}

	.access-control-face-empty-state{
		align-items: center;
		border: 1px dashed currentColor;
		border-radius: 10px;
		display: flex;
		flex: 1 1 auto;
		flex-direction: column;
		justify-content: center;
		min-height: 160px;
		padding: 20px;
		text-align: center;
	}

	.access-control-face-detail-card{
		border-radius: 12px;
	}

	.access-control-face-import-card{
		border-radius: 12px;
	}

	.access-control-face-detail-header{
		align-items: center;
		gap: 8px;
	}

	.access-control-face-detail-title{
		font-size: 1rem;
		font-weight: 700;
		line-height: 1.2;
	}

	.access-control-face-detail-body{
		display: grid;
		gap: 16px;
		grid-template-columns: 140px 1fr;
	}

	.access-control-face-detail-photo-wrap{
		align-items: center;
		display: flex;
		justify-content: center;
	}

	.access-control-face-detail-photo{
		border: thin solid currentColor;
		border-radius: 12px;
		display: block;
		height: 140px;
		object-fit: cover;
		width: 140px;
	}

	.access-control-face-detail-avatar{
		border: thin solid currentColor;
		border-radius: 12px !important;
	}

	.access-control-face-detail-list{
		display: flex;
		flex-direction: column;
		gap: 8px;
		min-width: 0;
	}

	.access-control-face-detail-row{
		align-items: center;
		border-bottom: thin solid currentColor;
		display: flex;
		gap: 12px;
		justify-content: space-between;
		padding-bottom: 6px;
	}

	@media (max-width: 960px){
		.access-control-face-toolbar{
			align-items: stretch;
			flex-direction: column;
		}

		.access-control-face-selection-bar{
			align-items: stretch;
			flex-direction: column;
			gap: 6px;
		}

		.access-control-face-search-wrap{
			flex-basis: auto;
			width: 100%;
		}
	}

	@media (max-width: 600px){
		.access-control-face-card-grid{
			grid-template-columns: 1fr;
		}

		.access-control-face-detail-body{
			grid-template-columns: 1fr;
		}

		.access-control-face-source-chip{
			max-width: 84px;
		}
	}
</style>
